import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { REGISTER_ROUTE } from '../../config'
import { getTopbarHeight, useDimensions } from '../../hooks/useDimensions'
import { AppBarComponent } from '../AppBar'
import { FooterBarComponent } from '../FooterBar'
import { UnitCard } from './components/UnitCard'
import { UnitsHero } from './components/UnitsHero'
import { UnitsSectionIntroBlock } from './components/UnitsSectionIntro'
import { StorageLayoutModal } from './LayoutModal'
import { getAvailableCount } from './helpers'
import { getUnitsMaster } from './selectors'
import { UnitsListingGrid, UnitsPageScrollArea, UnitsPageShell } from './styled'

export const UnitsComponent: React.FC = () => {
  const [open, setOpen] = React.useState(false)
  const { height, width } = useDimensions()
  const location = useLocation()
  const navigate = useNavigate()
  const unitsMaster = useSelector(getUnitsMaster)
  const topbarHeight = getTopbarHeight(width)

  const onOpen = () => setOpen(true)
  const onClose = () => setOpen(false)

  const onChangeRoute = React.useCallback(
    (path: string) => {
      if (path !== location.pathname) navigate(path)
    },
    [location.pathname, navigate]
  )

  const availableUnits = React.useMemo(
    () => unitsMaster.reduce((sum, unit) => sum + getAvailableCount(unit), 0),
    [unitsMaster]
  )

  const startingPrice = React.useMemo(() => {
    if (unitsMaster.length === 0) return null

    return Math.min(...unitsMaster.map((unit) => unit.price))
  }, [unitsMaster])

  const climateCount = React.useMemo(
    () => unitsMaster.filter((unit) => unit.isClimate).length,
    [unitsMaster]
  )

  const coveredCount = React.useMemo(
    () => unitsMaster.filter((unit) => unit.isCovered).length,
    [unitsMaster]
  )

  const reserveRoute = React.useCallback(
    () => onChangeRoute(REGISTER_ROUTE),
    [onChangeRoute]
  )

  return (
    <>
      <Helmet>
        <title>Units || JustLockIt</title>
        <meta name="keywords" content="HTML,CSS,JavaScript" />
        <meta
          name="description"
          content="justlockit.com units page. storage units Schulenburg, Texas"
        />
      </Helmet>
      <AppBarComponent />
      <UnitsPageScrollArea className="units-page" viewportHeight={height}>
        <UnitsPageShell
          className="units-page__shell"
          topbarHeight={topbarHeight}
        >
          <UnitsHero
            availableUnits={availableUnits}
            climateCount={climateCount}
            coveredCount={coveredCount}
            onReserve={reserveRoute}
            onViewLayout={onOpen}
          />

          <UnitsSectionIntroBlock startingPrice={startingPrice} />

          <UnitsListingGrid className="units-page__listing-grid">
            {unitsMaster.map((unit) => (
              <UnitCard
                key={unit.id ?? `${unit.size}-${unit.price}`}
                unit={unit}
                onReserve={reserveRoute}
              />
            ))}
          </UnitsListingGrid>
        </UnitsPageShell>
      </UnitsPageScrollArea>
      <FooterBarComponent />
      <StorageLayoutModal open={open} onClose={onClose} height={height} />
    </>
  )
}
